import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { AIEmployee, AIEmployeePlan } from '@/types/aiEmployee';

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------
export const aiEmployeeKeys = {
  all: ['ai-employees'] as const,
  list: (userId: string) => [...aiEmployeeKeys.all, 'list', userId] as const,
  detail: (id: string) => [...aiEmployeeKeys.all, 'detail', id] as const,
  plan: (userId: string) => [...aiEmployeeKeys.all, 'plan', userId] as const,
};

// ---------------------------------------------------------------------------
// Default plan (free tier) when edge function is unavailable
// ---------------------------------------------------------------------------
const DEFAULT_PLAN: AIEmployeePlan = {
  current_count: 0,
  max_count: 1,
  plan_name: 'Starter',
  can_add_more: true,
  upgrade_url: '/dashboard/billing',
};

// ---------------------------------------------------------------------------
// useAIEmployees
// ---------------------------------------------------------------------------
export function useAIEmployees() {
  const { user } = useAuth();

  return useQuery<AIEmployee[]>({
    queryKey: aiEmployeeKeys.list(user?.id ?? ''),
    queryFn: async (): Promise<AIEmployee[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('ai_employees')
        .select('*')
        .eq('user_id', user.id)
        .order('is_primary', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Auto-migrate: if no ai_employees rows but user has a legacy AI in user_settings, seed it
      if ((!data || data.length === 0)) {
        const { data: legacy } = await supabase
          .from('user_settings')
          .select('ai_name, ai_voice_id, business_name, vapi_phone_number')
          .eq('user_id', user.id)
          .single();

        const ls = legacy as { ai_name?: string; ai_voice_id?: string; business_name?: string; vapi_phone_number?: string } | null;
        if (ls?.ai_name) {
          const { data: seeded, error: seedErr } = await supabase
            .from('ai_employees')
            .insert({
              user_id: user.id,
              name: ls.ai_name,
              role: 'Receptionist',
              is_primary: true,
              is_active: true,
              personality: ls.business_name
                ? `Professional and friendly AI receptionist for ${ls.business_name}.`
                : '',
              voice_id: ls.ai_voice_id ?? 'voice_alex',
              phone_number: ls.vapi_phone_number ?? null,
              specialties: ['general', 'scheduling'],
              language: 'en',
              working_hours: { start: '09:00', end: '17:00', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
            })
            .select()
            .single();

          if (!seedErr && seeded) {
            return [seeded as AIEmployee];
          }
        }
      }

      return (data ?? []) as AIEmployee[];
    },
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// useAIEmployee (single)
// ---------------------------------------------------------------------------
export function useAIEmployee(id: string | null) {
  const { user } = useAuth();

  return useQuery<AIEmployee | null>({
    queryKey: aiEmployeeKeys.detail(id ?? ''),
    queryFn: async (): Promise<AIEmployee | null> => {
      if (!id || !user?.id) return null;

      const { data, error } = await supabase
        .from('ai_employees')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error || !data) return null;

      return data as AIEmployee;
    },
    enabled: !!user && !!id,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// useAIEmployeePlan
// ---------------------------------------------------------------------------
export function useAIEmployeePlan() {
  const { user } = useAuth();

  return useQuery<AIEmployeePlan>({
    queryKey: aiEmployeeKeys.plan(user?.id ?? ''),
    queryFn: async (): Promise<AIEmployeePlan> => {
      if (!user?.id) return { ...DEFAULT_PLAN };

      try {
        // Count current AI employees
        const { count, error: countError } = await supabase
          .from('ai_employees')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (countError) return { ...DEFAULT_PLAN };

        // Get user's plan from profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('subscription_plan_identifier')
          .eq('id', user.id)
          .single();

        const planName = profile?.subscription_plan_identifier || 'starter';
        const currentCount = count ?? 0;
        // Max 1 AI employee for all current plans (restriction must remain)
        const maxCount = 1;

        return {
          current_count: currentCount,
          max_count: maxCount,
          plan_name: planName,
          can_add_more: currentCount < maxCount,
          upgrade_url: '/dashboard/billing',
        };
      } catch {
        return { ...DEFAULT_PLAN };
      }
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ---------------------------------------------------------------------------
// useCreateAIEmployee
// ---------------------------------------------------------------------------
export function useCreateAIEmployee() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      employee: Omit<AIEmployee, 'id' | 'user_id' | 'calls_handled' | 'avg_satisfaction' | 'created_at' | 'updated_at'>,
    ) => {
      if (!user?.id) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('ai_employees')
        .insert({
          user_id: user.id,
          ...employee,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating AI employee:', error);
        throw error;
      }

      return data as AIEmployee;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.list(user?.id ?? '') });
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.plan(user?.id ?? '') });
      toast.success('AI Employee created');
    },
    onError: (err: Error) => {
      console.error('Error creating AI employee:', err);
      toast.error(`Failed to create AI employee: ${err.message}`);
    },
  });
}

// ---------------------------------------------------------------------------
// useUpdateAIEmployee
// ---------------------------------------------------------------------------
export function useUpdateAIEmployee() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<AIEmployee> & { id: string }) => {
      if (!user?.id) throw new Error('User ID is required');

      const { data, error } = await supabase
        .from('ai_employees')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating AI employee:', error);
        throw error;
      }

      return data as AIEmployee;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.list(user?.id ?? '') });
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.detail(data.id) });
      toast.success('AI Employee updated');
    },
    onError: (err: Error) => {
      console.error('Error updating AI employee:', err);
      toast.error(`Failed to update AI employee: ${err.message}`);
    },
  });
}

// ---------------------------------------------------------------------------
// useAutoProvisionPrimaryEmployee — creates a default employee if table empty
// ---------------------------------------------------------------------------
export function useAutoProvisionPrimaryEmployee() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (employeeName: string) => {
      if (!user?.id) throw new Error('User ID is required');

      // Check if any employees already exist
      const { data: existing, error: checkError } = await supabase
        .from('ai_employees')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (checkError) throw checkError;
      if (existing && existing.length > 0) {
        // Already has employees, skip
        return null;
      }

      // Check if user already has an AI employee configured in user_settings (legacy single-employee model)
      const { data: userSettings } = await supabase
        .from('user_settings')
        .select('ai_name, ai_voice_id, business_name, vapi_phone_number')
        .eq('user_id', user.id)
        .single();

      const legacy = userSettings as { ai_name?: string; ai_voice_id?: string; business_name?: string; vapi_phone_number?: string } | null;
      const hasLegacyEmployee = !!legacy?.ai_name;

      const { data, error } = await supabase
        .from('ai_employees')
        .insert({
          user_id: user.id,
          name: hasLegacyEmployee ? legacy.ai_name! : employeeName,
          role: 'Receptionist',
          is_primary: true,
          is_active: true,
          personality: legacy?.business_name
            ? `Professional and friendly AI receptionist for ${legacy.business_name}. Greets callers warmly and helps with scheduling, questions, and routing.`
            : 'Professional and friendly AI receptionist. Greets callers warmly and helps with scheduling, questions, and routing.',
          voice_id: hasLegacyEmployee ? (legacy.ai_voice_id ?? 'voice_alex') : 'voice_alex',
          phone_number: legacy?.vapi_phone_number ?? null,
          specialties: ['general', 'scheduling'],
          language: 'en',
          working_hours: { start: '09:00', end: '17:00', days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] },
        })
        .select()
        .single();

      if (error) {
        console.error('Error auto-provisioning AI employee:', error);
        throw error;
      }

      return data as AIEmployee;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.list(user?.id ?? '') });
        queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.plan(user?.id ?? '') });
      }
    },
    onError: (err: Error) => {
      console.error('Error auto-provisioning AI employee:', err);
    },
  });
}

// ---------------------------------------------------------------------------
// useDeleteAIEmployee
// ---------------------------------------------------------------------------
export function useDeleteAIEmployee() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('User ID is required');

      const { error } = await supabase
        .from('ai_employees')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting AI employee:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.list(user?.id ?? '') });
      queryClient.invalidateQueries({ queryKey: aiEmployeeKeys.plan(user?.id ?? '') });
      toast.success('AI Employee deleted');
    },
    onError: (err: Error) => {
      console.error('Error deleting AI employee:', err);
      toast.error(`Failed to delete AI employee: ${err.message}`);
    },
  });
}
